// Tabla de precios orientativos profesionales para Multiservicios PS 24 S.L.U.
// Precios base (Valencia y provincia) — revisados y orientativos para 2026.
// Multiplicadores visibles: estandar 1.00 | recomendado 1.10 (default) | premium 1.30

export const PRICE_LEVELS = [
  {
    id: 'estandar',
    label: 'Estándar profesional',
    factor: 1.0,
    hint: 'Trabajos normales, acceso sencillo, medición suficiente y sin dificultad especial. Ejecución profesional, herramientas, desplazamiento, personal asegurado y garantía básica.',
  },
  {
    id: 'recomendado',
    label: 'Recomendado',
    factor: 1.10,
    hint: 'Opción habitual: garantía completa, coordinación, remates cuidados, protección de la zona y gestión integral por parte de Multiservicios PS 24 S.L.U.',
  },
  {
    id: 'premium',
    label: 'Premium / Alta dificultad',
    factor: 1.25,
    hint: 'Difícil acceso, vivienda ocupada o amueblada, urgencia, baja medición, muchos remates, trabajos en altura, acabados superiores o mayor responsabilidad técnica.',
  },
];

export const EXTRAS = [
  { id: 'urgencia', label: 'Urgencia', pct: 0.20, desc: 'Plazo reducido +20%' },
  { id: 'pequeno', label: 'Trabajo pequeño / baja medición', pct: 0.15, desc: 'Mediciones bajas +15%' },
  { id: 'ocupada', label: 'Vivienda ocupada o amueblada', pct: 0.10, desc: 'Protección y trabajo con muebles +10%' },
  { id: 'acceso', label: 'Difícil acceso / sin ascensor', pct: 0.15, desc: 'Carga manual +15%' },
  { id: 'altura', label: 'Trabajo en altura o fachada', pct: 0.25, desc: 'Medios de elevación / EPI +25%' },
  { id: 'remates', label: 'Muchos remates', pct: 0.10, desc: 'Trabajo detallado +10%' },
];

export const FIXED_EXTRAS = [
  { id: 'escombro', label: 'Retirada especial de escombro', amount: 180, desc: 'Carga y gestión' },
  { id: 'contenedor', label: 'Contenedor de obra', amount: 280, desc: 'Alquiler y gestión' },
  { id: 'desplazamiento', label: 'Desplazamiento fuera de Valencia capital', amount: 120, desc: 'Provincia de Valencia' },
];

// Mínimos de intervención (aplicados si el subtotal queda por debajo)
export const MIN_INTERVENCIONES = {
  pequeno: 180,    // Trabajo pequeño básico
  media_jornada: 350,
  jornada: 650,
};
export const MIN_INTERVENTION = MIN_INTERVENCIONES.pequeno; // compat con código antiguo

// Etiquetas de materiales — comunes a todas las partidas
export const MATERIAL_TAGS = {
  basico: { label: 'Incluye material básico', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  manoObra: { label: 'Solo mano de obra', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  sinMaterial: { label: 'No incluye material principal', color: 'bg-amber-50 text-amber-800 border-amber-200' },
  aValorar: { label: 'Material a valorar aparte', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  desde: { label: 'Precio orientativo desde', color: 'bg-[#E6F0FA] text-[#004B87] border-[#004B87]/20' },
};

export const SERVICES = [
  {
    id: 'demoliciones',
    name: 'Demoliciones',
    icon: 'Hammer',
    description: 'Derribos, levantado de suelos y alicatados.',
    items: [
      { id: 'demo-hueco', name: 'Demolición tabique ladrillo hueco', unit: 'm²', price: 18, material: 'manoObra' },
      { id: 'demo-macizo', name: 'Demolición tabique ladrillo macizo', unit: 'm²', price: 22, material: 'manoObra' },
      { id: 'demo-pladur', name: 'Demolición tabique de pladur', unit: 'm²', price: 18, material: 'manoObra' },
      { id: 'demo-alicatado', name: 'Levantado alicatado / azulejo', unit: 'm²', price: 22, material: 'manoObra' },
      { id: 'demo-suelo', name: 'Levantado suelo cerámico / porcelánico', unit: 'm²', price: 25, material: 'manoObra' },
      { id: 'demo-compleja', name: 'Demolición interior compleja', unit: 'm²', price: 45, material: 'manoObra', incluye: ['Retirada de elementos'], noIncluye: ['Contenedor y retirada de escombros'] },
    ],
  },
  {
    id: 'pladur',
    name: 'Pladur / Obra Seca',
    icon: 'LayoutPanelLeft',
    description: 'Tabiques, falsos techos y trasdosados.',
    items: [
      { id: 'pl-estandar', name: 'Tabique pladur estándar (1+1)', unit: 'm²', price: 52, material: 'basico', incluye: ['Pladur estándar', 'Estructura metálica', 'Mano de obra'] },
      { id: 'pl-acustico', name: 'Tabique pladur con lana mineral (acústico)', unit: 'm²', price: 72, material: 'basico', incluye: ['Pladur', 'Estructura', 'Lana mineral', 'Mano de obra'] },
      { id: 'pl-hidrofugo', name: 'Tabique pladur hidrófugo (húmedos)', unit: 'm²', price: 65, material: 'basico' },
      { id: 'pl-techo', name: 'Falso techo continuo pladur estándar', unit: 'm²', price: 58, material: 'basico' },
      { id: 'pl-techo-basico', name: 'Falso techo continuo básico', unit: 'm²', price: 48, material: 'basico' },
      { id: 'pl-trasdosado-d', name: 'Trasdosado directo de pladur', unit: 'm²', price: 52, material: 'basico' },
      { id: 'pl-trasdosado-a', name: 'Trasdosado autoportante con lana mineral', unit: 'm²', price: 78, material: 'basico' },
    ],
  },
  {
    id: 'albanileria',
    name: 'Albañilería',
    icon: 'Brick',
    description: 'Tabiquería de ladrillo y bloque.',
    items: [
      { id: 'alb-hueco-s', name: 'Tabique ladrillo hueco sencillo', unit: 'm²', price: 55, material: 'basico' },
      { id: 'alb-hueco-d', name: 'Tabique ladrillo hueco doble', unit: 'm²', price: 68, material: 'basico' },
      { id: 'alb-macizo', name: 'Tabique ladrillo macizo', unit: 'm²', price: 85, material: 'basico' },
      { id: 'alb-bloque', name: 'Fábrica bloque hormigón estándar', unit: 'm²', price: 75, material: 'basico' },
      { id: 'alb-bloque-r', name: 'Muro bloque hormigón reforzado', unit: 'm²', price: 110, material: 'basico' },
      { id: 'alb-remates', name: 'Remates de albañilería (intervención mínima)', unit: 'ud', price: 180, material: 'basico' },
    ],
  },
  {
    id: 'yesos',
    name: 'Yesos y Morteros',
    icon: 'Trowel',
    description: 'Enlucidos, guarnecidos y monocapa.',
    items: [
      { id: 'yeso-bv', name: 'Enlucido de yeso a buena vista', unit: 'm²', price: 24, material: 'basico' },
      { id: 'yeso-guar', name: 'Guarnecido + enlucido de yeso', unit: 'm²', price: 38, material: 'basico' },
      { id: 'yeso-maestreado', name: 'Yeso maestreado', unit: 'm²', price: 36, material: 'basico' },
      { id: 'mort-basico', name: 'Enfoscado básico mortero cemento', unit: 'm²', price: 28, material: 'basico' },
      { id: 'mort-maestreado', name: 'Enfoscado maestreado mortero', unit: 'm²', price: 45, material: 'basico' },
      { id: 'mort-monocapa', name: 'Mortero monocapa fachada', unit: 'm²', price: 58, material: 'basico' },
    ],
  },
  {
    id: 'alicatados',
    name: 'Alicatados / Solados',
    icon: 'Grid3x3',
    description: 'Cerámica, porcelánico y gran formato.',
    items: [
      { id: 'ali-estandar', name: 'Alicatado cerámico estándar baño/cocina', unit: 'm²', price: 68, material: 'basico', noIncluye: ['Cerámica premium / gran formato'] },
      { id: 'ali-mo', name: 'Alicatado solo mano de obra estándar', unit: 'm²', price: 42, material: 'manoObra' },
      { id: 'ali-porcelanico', name: 'Alicatado porcelánico en pared', unit: 'm²', price: 85, material: 'basico' },
      { id: 'sol-gres', name: 'Solado gres cerámico estándar', unit: 'm²', price: 65, material: 'basico' },
      { id: 'sol-porc', name: 'Solado gres porcelánico calidad media', unit: 'm²', price: 82, material: 'basico' },
      { id: 'sol-gf', name: 'Porcelánico gran formato / rectificado', unit: 'm²', price: 120, material: 'basico' },
      { id: 'rodapie', name: 'Rodapié cerámico o porcelánico', unit: 'ml', price: 22, material: 'manoObra' },
    ],
  },
  {
    id: 'pavimentos',
    name: 'Pavimentos',
    icon: 'Square',
    description: 'Laminado, vinílico y nivelaciones.',
    items: [
      { id: 'pav-laminado-mo', name: 'Colocación suelo laminado / parquet (solo mano de obra)', unit: 'm²', price: 24, material: 'manoObra' },
      { id: 'pav-laminado-mat', name: 'Suelo laminado material incluido (gama media)', unit: 'm²', price: 58, material: 'basico' },
      { id: 'pav-vinil-mo', name: 'Suelo vinílico click (solo mano de obra)', unit: 'm²', price: 28, material: 'manoObra' },
      { id: 'pav-niv-b', name: 'Nivelación básica de suelo', unit: 'm²', price: 25, material: 'basico' },
      { id: 'pav-niv-c', name: 'Recrecido / mortero nivelación completo', unit: 'm²', price: 42, material: 'basico' },
    ],
  },
  {
    id: 'pintura',
    name: 'Pintura',
    icon: 'PaintRoller',
    description: 'Interior, exterior y tratamientos.',
    items: [
      { id: 'pin-int', name: 'Pintura interior paredes lisas (blanco/color)', unit: 'm²', price: 12, material: 'basico' },
      { id: 'pin-prem', name: 'Pintura interior premium paredes lisas', unit: 'm²', price: 18, material: 'basico' },
      { id: 'pin-techo', name: 'Pintura de techos', unit: 'm²', price: 18, material: 'basico' },
      { id: 'pin-masilla', name: 'Masilla + lija + pintura', unit: 'm²', price: 28, material: 'basico' },
      { id: 'pin-gotele', name: 'Quitar gotelé + alisar + pintar', unit: 'm²', price: 42, material: 'basico' },
      { id: 'pin-ext', name: 'Pintura exterior fachada', unit: 'm²', price: 32, material: 'basico' },
      { id: 'pin-anti', name: 'Pintura antihumedad / especial', unit: 'm²', price: 38, material: 'basico' },
    ],
  },
  {
    id: 'impermeabilizacion',
    name: 'Impermeabilización',
    icon: 'Droplets',
    description: 'Terrazas, cubiertas y fachadas.',
    items: [
      { id: 'imp-caucho', name: 'Impermeabilización terraza con caucho', unit: 'm²', price: 42, material: 'basico' },
      { id: 'imp-bi', name: 'Sistema bicomponente con malla', unit: 'm²', price: 52, material: 'basico' },
      { id: 'imp-asfaltica', name: 'Tela asfáltica', unit: 'm²', price: 68, material: 'basico' },
      { id: 'imp-transitable', name: 'Terraza transitable / pavimentada', unit: 'm²', price: 95, material: 'basico' },
      { id: 'imp-cubierta', name: 'Impermeabilización cubierta', unit: 'm²', price: 60, material: 'basico' },
      { id: 'imp-fachada', name: 'Impermeabilización fachada', unit: 'm²', price: 90, material: 'basico' },
    ],
  },
  {
    id: 'reformas-integrales',
    name: 'Reformas Integrales',
    icon: 'Home',
    description: 'Vivienda completa llave en mano.',
    items: [
      { id: 'ri-basica', name: 'Reforma integral básica vivienda', unit: 'm²', price: 950, material: 'basico', noIncluye: ['Acabados premium', 'Licencias', 'Mobiliario'] },
      { id: 'ri-media', name: 'Reforma integral media (Valencia)', unit: 'm²', price: 1250, material: 'basico' },
      { id: 'ri-alta', name: 'Reforma integral alta calidad / dificultad', unit: 'm²', price: 1600, material: 'basico' },
      { id: 'ri-premium', name: 'Reforma integral premium', unit: 'm²', price: 2000, material: 'basico' },
    ],
  },
  {
    id: 'bano-ducha',
    name: 'Baño / Plato de Ducha',
    icon: 'ShowerHead',
    description: 'Cambio de bañera, plato de ducha y reformas de baño.',
    items: [
      {
        id: 'bd-instalacion',
        name: 'A) Instalación plato de ducha (suministrado por cliente)',
        unit: 'ud', price: 350, material: 'manoObra',
        incluye: ['Colocación de plato suministrado por cliente', 'Ajuste básico', 'Conexión a desagüe existente preparado', 'Sellado'],
        noIncluye: ['Plato de ducha', 'Mampara', 'Grifería', 'Alicatado', 'Modificación importante de desagüe', 'Demolición de bañera', 'Retirada de escombros', 'Fontanería nueva'],
      },
      {
        id: 'bd-cambio-basico',
        name: 'B) Cambio de bañera por plato de ducha básico',
        unit: 'ud', price: 950, material: 'basico',
        incluye: ['Retirada de bañera', 'Preparación básica', 'Instalación plato ducha estándar', 'Adaptación básica de desagüe', 'Remate básico en zona afectada'],
        noIncluye: ['Mampara premium', 'Grifería premium', 'Alicatado completo del baño', 'Cambio completo de fontanería', 'Mueble de baño', 'Sanitarios'],
      },
      {
        id: 'bd-pack-completo',
        name: 'C) Pack cambio bañera por ducha completo',
        unit: 'ud', price: 1450, material: 'basico',
        incluye: ['Retirada de bañera', 'Plato de ducha estándar', 'Alicatado parcial zona afectada', 'Adaptación fontanería básica', 'Sellado y remates', 'Retirada básica de escombro'],
        noIncluye: ['Mampara y grifería (a valorar según modelo)', 'Reforma completa del baño'],
      },
      {
        id: 'bd-reforma-pequeno',
        name: 'D) Reforma completa baño pequeño básico', unit: 'ud', price: 4500, material: 'basico',
        incluye: ['Demolición', 'Fontanería básica', 'Electricidad básica', 'Alicatado y solado', 'Sanitarios estándar'],
        noIncluye: ['Mobiliario premium', 'Grifería empotrada'],
      },
      { id: 'bd-reforma-medio', name: 'D) Reforma completa baño medio estándar', unit: 'ud', price: 6500, material: 'basico' },
      { id: 'bd-reforma-alta', name: 'D) Reforma completa baño calidad media/alta', unit: 'ud', price: 8500, material: 'basico' },
      { id: 'bd-reforma-premium', name: 'D) Reforma completa baño premium / alta dificultad', unit: 'ud', price: 10000, material: 'basico' },
    ],
  },
  {
    id: 'fontaneria',
    name: 'Fontanería',
    icon: 'Droplet',
    description: 'Puntos de agua, sanitarios e instalaciones.',
    items: [
      {
        id: 'fo-punto-completo',
        name: 'Punto fontanería agua fría/caliente + desagüe',
        unit: 'punto', price: 250, material: 'basico',
        incluye: ['Tubería multicapa o similar', 'Conexión agua fría/caliente', 'Desagüe si aplica', 'Prueba estanqueidad', 'Mano de obra'],
        noIncluye: ['Sanitarios', 'Grifería', 'Muebles', 'Alicatado', 'Rozas complejas', 'Reparaciones estructurales', 'Bajantes generales'],
      },
      { id: 'fo-punto-sencillo', name: 'Punto de agua sencillo (lavadora, lavavajillas, toma cercana)', unit: 'punto', price: 180, material: 'basico' },
      {
        id: 'fo-instalacion-bano',
        name: 'Instalación completa fontanería baño',
        unit: 'ud', price: 1250, material: 'basico',
        incluye: ['Tomas agua fría/caliente', 'Desagües', 'Prueba estanqueidad'],
        noIncluye: ['Sanitarios y grifería', 'Alicatado', 'Acabados'],
      },
      { id: 'fo-instalacion-cocina', name: 'Instalación completa fontanería cocina', unit: 'ud', price: 750, material: 'basico' },
      { id: 'fo-bajante', name: 'Cambio de bajante / tramo desagüe', unit: 'ud', price: 180, material: 'basico' },
    ],
  },
  {
    id: 'electricidad',
    name: 'Electricidad',
    icon: 'Zap',
    description: 'Puntos eléctricos, cuadros y boletines.',
    items: [
      {
        id: 'el-punto-basico',
        name: 'Punto eléctrico básico',
        unit: 'punto', price: 85, material: 'basico',
        incluye: ['Cableado básico', 'Tubo si aplica', 'Mecanismo estándar blanco', 'Mano de obra'],
        noIncluye: ['Rozas complejas', 'Cuadro eléctrico', 'Boletín', 'Mecanismos premium', 'Reparaciones albañilería y pintura'],
      },
      { id: 'el-enchufe', name: 'Punto de enchufe adicional', unit: 'punto', price: 75, material: 'basico' },
      { id: 'el-luz', name: 'Punto de luz en techo o pared', unit: 'punto', price: 85, material: 'basico' },
      { id: 'el-interruptor', name: 'Interruptor / conmutado', unit: 'ud', price: 75, material: 'basico' },
      { id: 'el-linea', name: 'Línea dedicada (horno, vitro, termo, A/C)', unit: 'ud', price: 180, material: 'basico' },
      { id: 'el-cuadro', name: 'Cuadro eléctrico básico vivienda', unit: 'ud', price: 450, material: 'basico', noIncluye: ['Boletín CIE (valorar aparte)', 'Mecanismos premium', 'Rozas complejas'] },
      { id: 'el-boletin', name: 'Boletín de instalación eléctrica (CIE)', unit: 'ud', price: 195, material: 'basico' },
    ],
  },
];

export const COMPANY = {
  name: 'Multiservicios PS 24 S.L.U.',
  shortName: 'Multiservicios PS 24',
  tagline: 'Reformas, construcción, mantenimientos e impermeabilizaciones',
  slogan: 'Edificamos confianza, creando futuro.',
  area: 'Valencia y provincia',
  nif: 'B16465916',
  responsable: 'Pablo Andrés Segovia',
  whatsapp: '612236841',
  whatsappFormatted: '612 236 841',
  whatsappIntl: '34612236841',
  phone2: '722709952',
  phone2Formatted: '722 709 952',
  instagram: 'multiserviciosps24',
  email: 'multiserviciospsval@gmail.com',
  website: 'www.multiserviciosps24.com',
  logo: '/logo-cuadrado.png',
  actividad: 'Reformas, construcción, mantenimientos, pladur, alicatados, pavimentos, pintura, impermeabilizaciones, fontanería y electricidad.',
};

// Orden definitivo de servicios solicitado por la empresa.
const SERVICE_ORDER = [
  'bano-ducha',
  'fontaneria',
  'electricidad',
  'alicatados',
  'pladur',
  'pavimentos',
  'pintura',
  'impermeabilizacion',
  'albanileria',
  'yesos',
  'demoliciones',
  'reformas-integrales',
];
SERVICES.sort((a, b) => {
  const ia = SERVICE_ORDER.indexOf(a.id);
  const ib = SERVICE_ORDER.indexOf(b.id);
  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
});
